import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { TableHeader } from "~/components/ui/table";

import { getListProjectsQueryKey, listProjects } from "~/api/folders/folders";
import { GetOrganization200ServiceStatus } from "~/api/model";
import {
  useGetOrganization,
  useListOrganizations,
} from "~/api/organizations/organizations";
import { useGetCurrentUser } from "~/api/users/users";
import { Loader } from "~/components/Loader";
import { DEFAULT_FREE_TRIAL_LIMIT } from "~/features/billing/constants";
import { PaymentRequiredBanner } from "~/features/billing/PaymentRequiredBanner";
import { ROUTES, useOrganizationParams } from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { useFtue } from "~/hooks/useFtue";
import { CaseCard } from "./CaseCard";
import { CaseListItem } from "./CaseListItem";
import { CreateCaseButton } from "./CreateCaseButton";
import { EmptyCasesList } from "./EmptyCasesList";
import { FtueWelcome } from "./FtueWelcome";

interface TableHeader {
  id: string;
  defaultMessage: string;
  className?: string;
}

const ITEMS_PER_PAGE = 50;

const COLUMN_WIDTHS = {
  name: "w-[24%]",
  status: "w-[6%]",
  dateOfBirth: "w-[9%]",
  dateOfLoss: "w-[9%]",
  claimNumber: "w-[9%]",
  documents: "w-[5%]",
  created: "w-[9%]",
  modified: "w-[9%]",
  createdBy: "w-[15%]",
  actions: "w-[5%]",
} as const;

const DEMO_CASE_POLL_INTERVAL = 2000;
const DEMO_CASE_MAX_POLL_TIME = 60000;

export const CasesList: FC = () => {
  const navigate = useNavigate();
  const { organizationId } = useOrganizationParams();
  const observerTarget = useRef<HTMLDivElement>(null);
  const { data: currentUser } = useGetCurrentUser();
  const { shouldShowFtue, isLoading: isFtueLoading, demoCase } = useFtue();
  const queryClient = useQueryClient();
  const [isDemoCasePolling, setIsDemoCasePolling] = useState(false);
  const pollStartTimeRef = useRef<number | null>(null);
  const { isMobile, isLoading: isMobileLoading } = useMobile();
  const { data: organizations } = useListOrganizations();

  useEffect(() => {
    if (currentUser?.guestInOrganizations?.includes(organizationId)) {
      const homeOrg = organizations?.find(
        (org) => !currentUser.guestInOrganizations?.includes(org.id!)
      );
      if (homeOrg && homeOrg.id) {
        navigate(ROUTES.CASES({ organizationId: homeOrg.id }));
      } else {
        navigate(ROUTES.ACCESS_DENIED);
      }
    }
  }, [currentUser, navigate, organizationId, organizations]);

  const {
    data: casesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: getListProjectsQueryKey(organizationId, { archived: false }),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await listProjects(organizationId, {
        archived: false,
        page: pageParam as number,
        limit: ITEMS_PER_PAGE,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.pagination) return undefined;
      if (lastPage.pagination.currentPage < lastPage.pagination.totalPages) {
        return lastPage.pagination.currentPage + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  useEffect(() => {
    if (
      isError &&
      ((error as AxiosError)?.response?.status === 401 ||
        (error as AxiosError)?.response?.status === 403) &&
      currentUser?.guestInOrganizations?.includes(organizationId)
    ) {
      navigate(ROUTES.ACCESS_DENIED);
    }
  }, [isError, error, currentUser, navigate, organizationId]);

  const { data: organizationInfo } = useGetOrganization(
    organizationId as string
  );

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const option = {
      root: null,
      rootMargin: "0px",
      threshold: 0.5,
    };

    const observer = new IntersectionObserver(handleObserver, option);
    observer.observe(element);

    return () => observer.disconnect();
  }, [handleObserver]);

  const freeTrialLimit =
    Number(organizationInfo?.freeTrialLimit) || DEFAULT_FREE_TRIAL_LIMIT;

  const isPaymentRequired =
    ((organizationInfo?.pagesUsedInThisBillingPeriod ?? 0) >= freeTrialLimit &&
      organizationInfo?.serviceStatus ===
        GetOrganization200ServiceStatus.FREE_TRIAL) ||
    organizationInfo?.serviceStatus ===
      GetOrganization200ServiceStatus.PAYMENT_REQUIRED;

  const allCases = casesData?.pages.flatMap((page) => page.projects) || [];

  useEffect(() => {
    if (!shouldShowFtue || demoCase || isLoading) {
      setIsDemoCasePolling(false);
      pollStartTimeRef.current = null;
      return;
    }

    if (pollStartTimeRef.current === null) {
      pollStartTimeRef.current = Date.now();
    }

    const elapsedTime = Date.now() - pollStartTimeRef.current;
    if (elapsedTime >= DEMO_CASE_MAX_POLL_TIME) {
      setIsDemoCasePolling(false);
      return;
    }

    setIsDemoCasePolling(true);

    const pollTimer = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: getListProjectsQueryKey(organizationId, { archived: false }),
      });
    }, DEMO_CASE_POLL_INTERVAL);

    return () => clearTimeout(pollTimer);
  }, [
    shouldShowFtue,
    demoCase,
    isLoading,
    organizationId,
    queryClient,
    casesData,
  ]);

  const tableHeaders: TableHeader[] = [
    {
      id: "casesList.name",
      defaultMessage: "Name",
      className: COLUMN_WIDTHS.name,
    },
    {
      id: "casesList.status",
      defaultMessage: "Status",
      className: COLUMN_WIDTHS.status,
    },
    {
      id: "dateOfBirthLabel",
      defaultMessage: "Date of Birth",
      className: COLUMN_WIDTHS.dateOfBirth,
    },
    {
      id: "dateOfLossLabel",
      defaultMessage: "Date of Loss",
      className: COLUMN_WIDTHS.dateOfLoss,
    },
    {
      id: "claimNumberLabel",
      defaultMessage: "Claim/Case #",
      className: COLUMN_WIDTHS.claimNumber,
    },
    {
      id: "casesList.files",
      defaultMessage: "Files",
      className: COLUMN_WIDTHS.documents,
    },
    {
      id: "casesList.created",
      defaultMessage: "Created",
      className: COLUMN_WIDTHS.created,
    },
    {
      id: "casesList.modified",
      defaultMessage: "Last Modify",
      className: COLUMN_WIDTHS.modified,
    },
    {
      id: "casesList.createdBy",
      defaultMessage: "Created By",
      className: COLUMN_WIDTHS.createdBy,
    },
    { id: "", defaultMessage: "", className: COLUMN_WIDTHS.actions },
  ];

  const onCaseClick = (caseId?: string) =>
    caseId && navigate(ROUTES.CASE({ organizationId, caseId }));

  if (isLoading || isFtueLoading || isMobileLoading)
    return (
      <div className="flex justify-center items-center h-full">
        <Loader className="h-[calc(100vh-140px)]" />
      </div>
    );

  if (isError)
    return (
      <div className="flex justify-center items-center h-full">
        <p>
          <FormattedMessage
            id="casesList.failedFetchingCases"
            defaultMessage={
              "Couldn't fetch the list of cases. Please try again"
            }
          />
        </p>
      </div>
    );

  const isFirstCase = allCases.length === 0;

  if (shouldShowFtue) {
    return (
      <>
        {isPaymentRequired && (
          <div className="p-6">
            <PaymentRequiredBanner />
          </div>
        )}
        <FtueWelcome
          demoCase={demoCase}
          isDemoCaseLoading={isDemoCasePolling}
        />
      </>
    );
  }

  if (isFirstCase)
    return (
      <>
        {isPaymentRequired && (
          <div className="p-6">
            <PaymentRequiredBanner />
          </div>
        )}
        <EmptyCasesList disabled={isPaymentRequired} />
      </>
    );

  return (
    <div>
      {isPaymentRequired && (
        <div className="p-4 sm:p-6">
          <PaymentRequiredBanner />
        </div>
      )}
      <div className="flex justify-between items-center px-4 sm:px-6 pt-4 sm:pt-6 pb-4 sm:pb-8">
        <h2 className="text-xl sm:text-2xl font-semibold">
          <FormattedMessage id="casesList.title" defaultMessage={"Cases"} />
        </h2>
        <div className="cursor-pointer">
          <CreateCaseButton
            disabled={isPaymentRequired}
            isFirstCase={isFirstCase}
          />
        </div>
      </div>

      {isMobile ? (
        <div className="px-0">
          {casesData?.pages.map((page, i) => (
            <React.Fragment key={i}>
              {(Array.isArray(page) ? page : page?.projects || []).map(
                (caseItem) => (
                  <CaseCard
                    key={caseItem.id}
                    caseItem={caseItem}
                    onCaseClick={onCaseClick}
                  />
                )
              )}
            </React.Fragment>
          ))}
          <div ref={observerTarget} className="h-10" />
          {isFetchingNextPage && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-md px-6">
          <div className="flex items-center border-b bg-muted py-3 rounded-tl-lg rounded-tr-lg">
            {tableHeaders.map((header) => (
              <div
                key={header.id}
                className={`px-4 text-sm font-medium text-muted-foreground ${header.className}`}
              >
                {header.id && (
                  <FormattedMessage
                    id={header.id}
                    defaultMessage={header.defaultMessage}
                  />
                )}
              </div>
            ))}
          </div>
          <div>
            {casesData?.pages.map((page, i) => (
              <React.Fragment key={i}>
                {(Array.isArray(page) ? page : page?.projects || []).map(
                  (caseItem) => (
                    <CaseListItem
                      key={caseItem.id}
                      caseItem={caseItem}
                      onCaseClick={onCaseClick}
                      columnWidths={COLUMN_WIDTHS}
                    />
                  )
                )}
              </React.Fragment>
            ))}
            <div ref={observerTarget} className="h-10" />
            {isFetchingNextPage && (
              <div className="flex justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
